/**
 * Custom Field Validation
 *
 * Validation utilities for dynamic custom fields based on field definitions.
 */

import type { CustomFieldDefinition, ValidationRule } from './mockData/jobTypes';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate a single custom field value against its definition
 */
export function validateCustomField(
  value: unknown,
  definition: CustomFieldDefinition
): ValidationResult {
  // Check required fields
  if (definition.isRequired && (value === undefined || value === null || value === '')) {
    return {
      isValid: false,
      error: `${definition.label} is required`,
    };
  }

  // Skip validation for empty optional fields
  if (!definition.isRequired && (value === undefined || value === null || value === '')) {
    return { isValid: true };
  }

  // Type-specific validation
  switch (definition.fieldType) {
    case 'number':
      return validateNumber(value, definition);
    case 'text':
      return validateText(value, definition);
    case 'select':
      return validateSelect(value, definition);
    case 'multiselect':
      return validateMultiselect(value, definition);
    case 'date':
      return validateDate(value, definition);
    case 'boolean':
      return validateBoolean(value, definition);
    default:
      return { isValid: true };
  }
}

/**
 * Validate all custom fields against job type definitions
 */
export function validateCustomFields(
  values: Record<string, unknown>,
  definitions: CustomFieldDefinition[]
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const definition of definitions) {
    if (!definition.isActive) continue;

    const value = values[definition.name];
    const result = validateCustomField(value, definition);

    if (!result.isValid && result.error) {
      errors[definition.name] = result.error;
    }
  }

  return errors;
}

// Type-specific validators

function validateNumber(value: unknown, definition: CustomFieldDefinition): ValidationResult {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);

  if (isNaN(num)) {
    return {
      isValid: false,
      error: `${definition.label} must be a valid number`,
    };
  }

  // Apply validation rules
  if (definition.validationRules) {
    for (const rule of definition.validationRules) {
      const ruleResult = applyNumberValidationRule(num, rule, definition.label);
      if (!ruleResult.isValid) {
        return ruleResult;
      }
    }
  }

  return { isValid: true };
}

function validateText(value: unknown, definition: CustomFieldDefinition): ValidationResult {
  const text = String(value);

  // Apply validation rules
  if (definition.validationRules) {
    for (const rule of definition.validationRules) {
      const ruleResult = applyTextValidationRule(text, rule, definition.label);
      if (!ruleResult.isValid) {
        return ruleResult;
      }
    }
  }

  return { isValid: true };
}

function validateSelect(value: unknown, definition: CustomFieldDefinition): ValidationResult {
  const selected = String(value);

  if (!definition.options) {
    return { isValid: true };
  }

  if (!definition.options.includes(selected)) {
    return {
      isValid: false,
      error: `${definition.label} must be one of the available options`,
    };
  }

  return { isValid: true };
}

function validateMultiselect(value: unknown, definition: CustomFieldDefinition): ValidationResult {
  if (!Array.isArray(value)) {
    return {
      isValid: false,
      error: `${definition.label} must be a list of values`,
    };
  }

  if (!definition.options) {
    return { isValid: true };
  }

  const invalidValues = value.filter((v) => !definition.options?.includes(String(v)));

  if (invalidValues.length > 0) {
    return {
      isValid: false,
      error: `${definition.label} contains invalid options`,
    };
  }

  return { isValid: true };
}

function validateDate(value: unknown, definition: CustomFieldDefinition): ValidationResult {
  const dateStr = String(value);
  const date = new Date(dateStr);

  if (isNaN(date.getTime())) {
    return {
      isValid: false,
      error: `${definition.label} must be a valid date`,
    };
  }

  return { isValid: true };
}

function validateBoolean(value: unknown, definition: CustomFieldDefinition): ValidationResult {
  if (typeof value !== 'boolean') {
    return {
      isValid: false,
      error: `${definition.label} must be true or false`,
    };
  }

  return { isValid: true };
}

// Validation rule appliers

function applyNumberValidationRule(
  num: number,
  rule: ValidationRule,
  label: string
): ValidationResult {
  switch (rule.type) {
    case 'min':
      if (num < Number(rule.value)) {
        return {
          isValid: false,
          error: rule.message || `${label} must be at least ${rule.value}`,
        };
      }
      break;
    case 'max':
      if (num > Number(rule.value)) {
        return {
          isValid: false,
          error: rule.message || `${label} must be at most ${rule.value}`,
        };
      }
      break;
  }

  return { isValid: true };
}

function applyTextValidationRule(
  text: string,
  rule: ValidationRule,
  label: string
): ValidationResult {
  switch (rule.type) {
    case 'minLength':
      if (text.length < Number(rule.value)) {
        return {
          isValid: false,
          error: rule.message || `${label} must be at least ${rule.value} characters`,
        };
      }
      break;
    case 'maxLength':
      if (text.length > Number(rule.value)) {
        return {
          isValid: false,
          error: rule.message || `${label} must be at most ${rule.value} characters`,
        };
      }
      break;
    case 'pattern': {
      const regex = new RegExp(String(rule.value));
      if (!regex.test(text)) {
        return {
          isValid: false,
          error: rule.message || `${label} format is invalid`,
        };
      }
      break;
    }
  }

  return { isValid: true };
}
